/**
 * button.jsx — Polymorphic button primitive with variant and size props.
 *
 * Exports: Button
 *
 * Variants: default (primary fill), destructive, outline, secondary.
 * Sizes:    default (h-10), sm (h-9), lg (h-11).
 *
 * Accepts a ref and all standard <button> HTML attributes. Extra className
 * values are merged with cn() so callers can safely extend styles.
 */
import * as React from "react"
import { cn } from "../../lib/utils"

/**
 * Button — Styled button component with configurable visual variants and sizes.
 *
 * @param {string}                             [props.className]        - Additional Tailwind classes.
 * @param {'default'|'destructive'|'outline'|'secondary'} [props.variant='default'] - Visual style.
 * @param {'default'|'sm'|'lg'}                [props.size='default']   - Height/padding preset.
 * @param {React.Ref}                          ref                      - Forwarded ref.
 */
const Button = React.forwardRef(({ className, variant = "default", size = "default", ...props }, ref) => {
  return (
    <button
      className={cn(
        // Base styles shared by all variants and sizes.
        "inline-flex items-center justify-center rounded-md text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50",
        // Variant-specific colour styles.
        {
          "bg-primary text-primary-foreground hover:bg-primary/90": variant === "default",
          "bg-destructive text-destructive-foreground hover:bg-destructive/90": variant === "destructive",
          "border border-input bg-background hover:bg-accent hover:text-accent-foreground": variant === "outline",
          "bg-secondary text-secondary-foreground hover:bg-secondary/80": variant === "secondary",
        },
        // Size-specific height and padding styles.
        {
          "h-10 px-4 py-2": size === "default",
          "h-9 rounded-md px-3": size === "sm",
          "h-11 rounded-md px-8": size === "lg",
        },
        className
      )}
      ref={ref}
      {...props}
    />
  )
})

Button.displayName = "Button"

export { Button }
